<script setup lang="ts">
import { computed } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useI18n, type I18nKey } from '@/i18n'
import type { FillLayer, FillPaint } from '@/types/document'
import SliderField from '@/components/common/SliderField.vue'
import GradientEditor from '@/components/common/GradientEditor.vue'

const props = defineProps<{ layer: FillLayer }>()
const docStore = useDocumentStore()
const { t } = useI18n()
const fill = computed(() => props.layer.fill)
const grad = computed(() => props.layer.fill as Extract<FillPaint, { kind: 'gradient' }>)
let inTx = false
function set(next: FillPaint, commit: boolean) {
  if (!commit && !inTx) {
    docStore.beginTransaction('history.fillProps')
    inTx = true
  }
  docStore.setFill(props.layer.id, next)
  if (commit && inTx) {
    docStore.commitTransaction()
    inTx = false
  }
}
</script>

<template>
  <div class="fill">
    <template v-if="fill.kind === 'solid'">
      <label class="row"><span class="muted">{{ t('fill.color') }}</span><input type="color" :value="fill.hex" @input="set({ kind: 'solid', hex: ($event.target as HTMLInputElement).value }, false)" @change="set({ kind: 'solid', hex: ($event.target as HTMLInputElement).value }, true)" /></label>
    </template>
    <template v-else-if="fill.kind === 'pattern'">
      <label class="row"><span class="muted">{{ t('pattern.title') }}</span>
        <select :value="fill.patternId" @change="set({ ...fill, patternId: ($event.target as HTMLSelectElement).value }, true)">
          <option v-for="p in docStore.doc?.patterns ?? []" :key="p.id" :value="p.id">{{ p.name }} ({{ p.w }}×{{ p.h }})</option>
        </select>
      </label>
      <SliderField :label="t('fill.scale')" :value="fill.scale" :min="0.05" :max="10" :step="0.01" @input="v => set({ ...(fill as Extract<FillPaint, { kind: 'pattern' }>), scale: v }, false)" @change="v => set({ ...(fill as Extract<FillPaint, { kind: 'pattern' }>), scale: v }, true)" />
      <label class="row"><span class="muted">X / Y</span><input type="number" class="num" :value="fill.offset.x" @change="set({ ...fill, offset: { x: Number(($event.target as HTMLInputElement).value) || 0, y: fill.offset.y } }, true)" /><input type="number" class="num" :value="fill.offset.y" @change="set({ ...fill, offset: { x: fill.offset.x, y: Number(($event.target as HTMLInputElement).value) || 0 } }, true)" /></label>
    </template>
    <template v-else>
      <label class="row"><span class="muted">{{ t('fill.style') }}</span>
        <select :value="grad.style" @change="set({ ...grad, style: ($event.target as HTMLSelectElement).value as 'linear' }, true)">
          <option v-for="st in ['linear', 'radial', 'angle', 'reflected', 'diamond']" :key="st" :value="st">{{ t(`options.gradient.${st}` as I18nKey) }}</option>
        </select>
      </label>
      <GradientEditor :stops="grad.stops" with-alpha @input="st => set({ ...grad, stops: st.map(s => ({ pos: s.pos, hex: s.hex, alpha: s.alpha ?? 1 })) }, false)" @change="st => set({ ...grad, stops: st.map(s => ({ pos: s.pos, hex: s.hex, alpha: s.alpha ?? 1 })) }, true)" />
      <SliderField :label="t('fill.angle')" :value="grad.angle" :min="-180" :max="180" unit="°" @input="v => set({ ...grad, angle: v }, false)" @change="v => set({ ...grad, angle: v }, true)" />
      <SliderField :label="t('fill.scale')" :value="grad.scale" :min="0.1" :max="5" :step="0.01" @input="v => set({ ...grad, scale: v }, false)" @change="v => set({ ...grad, scale: v }, true)" />
      <label class="row"><input type="checkbox" :checked="grad.reverse" @change="set({ ...grad, reverse: ($event.target as HTMLInputElement).checked }, true)" /><span>{{ t('adj.reverse') }}</span></label>
    </template>
  </div>
</template>

<style scoped>
.fill {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
</style>
